import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import { toast, type BnBadgeVariant, type BnColumn, type BnRow } from "../components/index.js";
import { navigate } from "../nav.js";
import { detailedError, humanError } from "../api/errors.js";
import { subscribeLive } from "../api/realtime.js";
import {
  getRepository,
  listRepositorySecrets,
  setRepositorySecret,
  deleteRepositorySecret,
  exportRepository,
  type RepositoryDetail,
  type RepositorySecretName,
} from "../api/repositories.js";
import { getDataArea } from "../api/data.js";
import { listRepositoryDeployments } from "../api/deployments.js";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * page-repository (/repositories/:id) shows one git repository: where its
 * history lives (hosted here or mirrored from a forge), the clone URL, its size
 * against the limit, its branch workspaces, the secrets every branch inherits,
 * and a zip export. It is linked from the header of each branch workspace.
 * Sync state changes arrive over realtime on the repository row; the Refresh
 * button covers a stream that could not be set up.
 */
@customElement("page-repository")
export class PageRepository extends LitElement {
  @property() repositoryId = "";

  @state() private repo: RepositoryDetail | null = null;
  @state() private loading = true;
  @state() private error = "";
  @state() private exporting = false;
  /** Whether the server has the data area on; decides the Data link. */
  @state() private dataArea = false;
  /** Whether the server has deployments on; decides the Deployments link. */
  @state() private deploymentsOn = false;

  @state() private secrets: RepositorySecretName[] = [];
  @state() private secretsLoading = false;
  @state() private showAddSecret = false;
  @state() private secretName = "";
  @state() private secretValue = "";
  @state() private secretError = "";
  @state() private savingSecret = false;
  @state() private pendingSecretDelete = "";

  private stopLive?: () => void;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
      flex-wrap: wrap;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
    }
    .spacer {
      flex: 1;
    }
    .sections {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .meta {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
      gap: var(--bino-space-md);
    }
    .meta .k {
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-text-secondary);
      margin-bottom: var(--bino-space-xs);
    }
    .meta .v {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      word-break: break-all;
    }
    .hint {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin: 0 0 var(--bino-space-md);
    }
    .err {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-error);
      margin: var(--bino-space-md) 0 0;
    }
    .copy-row {
      display: flex;
      align-items: stretch;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-sm);
    }
    .code {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      margin: 0;
      padding: var(--bino-space-sm) var(--bino-space-md);
      background: var(--bino-surface-inset);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
      white-space: nowrap;
      overflow-x: auto;
    }
    .warn {
      display: flex;
      gap: var(--bino-space-sm);
      align-items: flex-start;
      margin-top: var(--bino-space-md);
      padding: var(--bino-space-md);
      background: var(--bino-warning-bg);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      font-size: var(--bino-font-size-base);
      color: var(--bino-warning-text);
    }
    .warn-icon {
      flex-shrink: 0;
      font-weight: 700;
    }
    .mono {
      font-family: var(--bino-font-mono);
    }
    a {
      color: var(--bino-primary);
    }
  `;

  override updated(changed: PropertyValues) {
    if (changed.has("repositoryId") && this.repositoryId) void this.load();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stopSubscription();
  }

  private async load() {
    const id = this.repositoryId;
    // Left the page, or moved to another repository, while the read was in
    // flight: a subscription started now would never be stopped.
    const stale = () => !this.isConnected || id !== this.repositoryId;
    this.loading = true;
    this.error = "";
    try {
      const repo = await getRepository(id);
      if (stale()) return;
      this.repo = repo;
    } catch (e) {
      if (stale()) return;
      this.error = humanError(e);
      this.loading = false;
      return;
    }
    this.loading = false;
    this.startSubscription();
    void this.loadSecrets();
    void this.probeData(id);
    void this.probeDeployments(id);
  }

  /**
   * probeData reads the data area once to learn whether the server has it
   * on. A failure keeps the link hidden: the page works without it.
   */
  private async probeData(id: string) {
    try {
      const area = await getDataArea(id);
      if (id === this.repositoryId) this.dataArea = area !== null;
    } catch {
      /* hidden */
    }
  }

  /** probeDeployments is probeData for the deployments. */
  private async probeDeployments(id: string) {
    try {
      const deps = await listRepositoryDeployments(id);
      if (id === this.repositoryId) this.deploymentsOn = deps !== null;
    } catch {
      /* hidden */
    }
  }

  /** refresh re-reads the repository without blanking the page. */
  private async refresh() {
    try {
      this.repo = await getRepository(this.repositoryId);
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  private startSubscription() {
    this.stopSubscription();
    this.stopLive = subscribeLive({
      collection: "repositories",
      topic: this.repositoryId,
      fields: "id,last_sync_status",
      onEvent: () => void this.refresh(),
      // No poll fallback: the page has a Refresh button.
      onDown: () => {},
    });
  }

  private stopSubscription() {
    this.stopLive?.();
    this.stopLive = undefined;
  }

  private async copy(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast(label, "success");
    } catch {
      toast("Couldn't copy. Select the text and copy it yourself.", "error");
    }
  }

  private async exportZip() {
    this.exporting = true;
    try {
      const { blob, filename } = await exportRepository(this.repositoryId);
      downloadBlob(blob, filename);
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.exporting = false;
    }
  }

  // ---- Secrets ----

  private async loadSecrets() {
    this.secretsLoading = true;
    try {
      this.secrets = await listRepositorySecrets(this.repositoryId);
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.secretsLoading = false;
    }
  }

  private async submitSecret() {
    const name = this.secretName.trim();
    if (!name) {
      this.secretError = "Enter a secret name.";
      return;
    }
    if (!this.secretValue) {
      this.secretError = "Enter a value.";
      return;
    }
    this.savingSecret = true;
    this.secretError = "";
    try {
      await setRepositorySecret(this.repositoryId, name, this.secretValue);
      // Reload: a name the team also defines flips that row to repository scope.
      await this.loadSecrets();
      this.showAddSecret = false;
      this.secretName = "";
      this.secretValue = "";
      toast("Secret saved", "success");
    } catch (e) {
      // A 400 names the problem, e.g. a name the cluster reserves.
      this.secretError = detailedError(e);
    } finally {
      this.savingSecret = false;
    }
  }

  private async confirmSecretDelete() {
    const name = this.pendingSecretDelete;
    try {
      await deleteRepositorySecret(this.repositoryId, name);
      // Reload: removing an override can uncover a team secret of that name.
      await this.loadSecrets();
      toast("Secret deleted", "success");
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.pendingSecretDelete = "";
    }
  }

  // ---- Render ----

  override render() {
    if (this.loading && !this.repo) {
      return html`<bn-spinner block label="Loading repository…"></bn-spinner>`;
    }
    if (this.error || !this.repo) {
      return html`<bn-empty-state
        icon="⚠"
        heading="Couldn’t load repository"
        message=${this.error || "Repository not found."}
      >
        <bn-button slot="actions" variant="secondary" @click=${() => navigate("/workspaces")}
          >Back to workspaces</bn-button
        >
      </bn-empty-state>`;
    }
    const r = this.repo;
    return html`
      <div class="head">
        <h1>${r.name}</h1>
        <bn-badge variant=${r.origin === "mirror" ? "info" : "neutral"}>${r.origin}</bn-badge>
        ${r.overQuota
          ? html`<bn-badge variant="warning">over size or file limit</bn-badge>`
          : nothing}
        <span class="spacer"></span>
        <bn-button variant="ghost" size="sm" @click=${() => this.refresh()}>Refresh</bn-button>
        ${this.dataArea
          ? html`<bn-button
              variant="secondary"
              size="sm"
              @click=${() => navigate(`/repositories/${encodeURIComponent(r.id)}/data`)}
              >Data</bn-button
            >`
          : nothing}
        ${this.deploymentsOn
          ? html`<bn-button
              variant="secondary"
              size="sm"
              @click=${() => navigate(`/repositories/${encodeURIComponent(r.id)}/deployments`)}
              >Deployments</bn-button
            >`
          : nothing}
        <bn-button
          variant="secondary"
          size="sm"
          ?loading=${this.exporting}
          @click=${() => this.exportZip()}
          >Export</bn-button
        >
      </div>

      <div class="sections">
        ${this.renderDetails(r)} ${this.renderClone(r)} ${this.renderBranches(r)}
        ${this.renderSecrets()}
      </div>

      ${this.renderModals()}
    `;
  }

  private renderDetails(r: RepositoryDetail) {
    return html`<bn-card heading="Details">
      <div class="meta">
        <div>
          <div class="k">Origin</div>
          <div class="v">${r.origin === "mirror" ? "Mirror of a git forge" : "Hosted here"}</div>
        </div>
        <div><div class="k">Default branch</div><div class="v">${r.defaultBranch}</div></div>
        <div><div class="k">On disk</div><div class="v">${formatBytes(r.sizeBytes)}</div></div>
        <div><div class="k">Created</div><div class="v">${formatTime(r.created)}</div></div>
      </div>
      ${r.overQuota
        ? html`<div class="warn">
            <span class="warn-icon" aria-hidden="true">!</span>
            ${r.origin === "mirror"
              ? html`<span
                  >The default branch of this repository is over the size or file limit, and no sync
                  from the forge has replaced it yet. Reads, builds and previews still work.</span
                >`
              : html`<span
                  >The default branch of this repository is over the size or file limit. Reads,
                  builds and previews still work. Until it is below the limits again, only changes
                  to the default branch that do not make it larger are accepted, plus deletes of
                  other branches.</span
                >`}
          </div>`
        : nothing}
      ${r.origin === "mirror" ? this.renderSync(r) : nothing}
    </bn-card>`;
  }

  private renderSync(r: RepositoryDetail) {
    const variant: BnBadgeVariant =
      r.lastSyncStatus === "succeeded"
        ? "success"
        : r.lastSyncStatus === "failed"
          ? "error"
          : "info";
    const live = r.lastSyncStatus === "pending" || r.lastSyncStatus === "syncing";
    return html`<div class="meta" style="margin-top:var(--bino-space-md)">
        <div><div class="k">Forge</div><div class="v">${r.repoUrl ?? "—"}</div></div>
        <div>
          <div class="k">Last sync</div>
          <div class="v">
            ${r.lastSyncStatus
              ? html`<bn-badge .variant=${variant} ?dot=${live}>${r.lastSyncStatus}</bn-badge>`
              : nothing}
            ${formatTime(r.lastSyncAt)}
          </div>
        </div>
        <div><div class="k">Skipped branches</div><div class="v">${r.skippedBranches}</div></div>
      </div>
      ${r.lastSyncStatus === "failed" && r.lastSyncError
        ? html`<p class="err">${r.lastSyncError}</p>`
        : nothing}
      ${r.skippedBranches > 0
        ? html`<p class="hint" style="margin:var(--bino-space-md) 0 0">
            ${r.skippedBranches} forge
            ${r.skippedBranches === 1 ? "branch has" : "branches have"} no workspace because the
            repository reached its branch limit.
          </p>`
        : nothing}`;
  }

  private renderClone(r: RepositoryDetail) {
    const cmd = `git clone ${r.cloneUrl}`;
    const push = `git push -u origin HEAD:${r.defaultBranch}`;
    return html`<bn-card heading="Clone">
      <p class="hint">
        ${r.origin === "mirror"
          ? "Clone over HTTPS. This repository mirrors a git forge, so push there, not here."
          : `Clone and push over HTTPS. The first push must create the default branch
            ${r.defaultBranch}, whatever git named the branch of your clone.`}
        Use a fine-grained token for this repository with the Git permission as the password.
      </p>
      <div class="copy-row">
        <span class="code">${cmd}</span>
        <bn-button variant="secondary" size="sm" @click=${() => void this.copy(cmd, "Command copied")}
          >Copy</bn-button
        >
      </div>
      ${r.origin === "mirror"
        ? nothing
        : html`<div class="copy-row">
            <span class="code">${push}</span>
            <bn-button variant="secondary" size="sm" @click=${() => void this.copy(push, "Command copied")}
              >Copy</bn-button
            >
          </div>`}
    </bn-card>`;
  }

  private renderBranches(r: RepositoryDetail) {
    return html`<bn-card heading="Branches" flush>
      <bn-table
        .columns=${[
          { key: "branch", label: "Branch", mono: true },
          { key: "flags", label: "", width: "1px" },
          { key: "commit", label: "Commit", mono: true, width: "1px" },
          { key: "lastPush", label: "Last change", width: "1px" },
        ] as BnColumn[]}
        .rows=${r.branches.map((b): BnRow => {
          const href = `/workspaces/${encodeURIComponent(b.workspaceId)}`;
          return {
            branch: html`<a
              href=${href}
              @click=${(e: Event) => {
                e.preventDefault();
                navigate(href);
              }}
              >${b.branch}</a
            >`,
            flags: html`${b.branch === r.defaultBranch ? html`<bn-badge>default</bn-badge>` : nothing}
            ${b.pinned ? html`<bn-badge>pinned</bn-badge>` : nothing}`,
            commit: b.commit ? b.commit.slice(0, 8) : "—",
            lastPush: formatTime(b.lastPushAt),
          };
        })}
        empty="No branch workspaces yet."
      ></bn-table>
    </bn-card>`;
  }

  private renderSecrets() {
    return html`<bn-card heading="Secrets" flush>
      <bn-button
        slot="actions"
        size="sm"
        variant="primary"
        @click=${() => {
          this.secretName = "";
          this.secretValue = "";
          this.secretError = "";
          this.showAddSecret = true;
        }}
        >Add secret</bn-button
      >
      ${this.secretsLoading && this.secrets.length === 0
        ? html`<bn-spinner block label="Loading secrets…"></bn-spinner>`
        : this.secrets.length === 0
          ? html`<bn-empty-state
              icon="🔑"
              heading="No secrets"
              message="Builds of every branch workspace get repository secrets as environment variables."
            ></bn-empty-state>`
          : html`<bn-table
              .columns=${[
                { key: "name", label: "Name", mono: true },
                { key: "scope", label: "Scope", width: "1px" },
                { key: "actions", label: "", align: "right", width: "1px" },
              ] as BnColumn[]}
              .rows=${this.secrets.map(
                (s): BnRow => ({
                  name: s.reserved
                    ? html`${s.name}
                        <bn-badge variant="warning">reserved, builds ignore it</bn-badge>`
                    : s.name,
                  scope:
                    s.scope === "team"
                      ? html`<bn-badge variant="info">team</bn-badge>`
                      : html`<bn-badge variant="success">repository</bn-badge>`,
                  // Team secrets are managed on the team page.
                  actions:
                    s.scope === "repository"
                      ? html`<bn-button
                          size="sm"
                          variant="danger"
                          @click=${() => (this.pendingSecretDelete = s.name)}
                          >Delete</bn-button
                        >`
                      : nothing,
                }),
              )}
            ></bn-table>`}
    </bn-card>`;
  }

  private renderModals() {
    return html`
      <bn-modal
        ?open=${this.showAddSecret}
        heading="Add secret"
        @close=${() => (this.showAddSecret = false)}
      >
        <bn-field label="Name" required hint="e.g. API_TOKEN" error=${this.secretError}>
          <bn-input
            mono
            .value=${this.secretName}
            placeholder="API_TOKEN"
            @input=${(e: CustomEvent<{ value: string }>) => (this.secretName = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Value" required hint="Write-only. It is never shown again.">
          <bn-input
            type="password"
            .value=${this.secretValue}
            @input=${(e: CustomEvent<{ value: string }>) => (this.secretValue = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.showAddSecret = false)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${this.savingSecret}
          @click=${() => this.submitSecret()}
          >Save</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.pendingSecretDelete !== ""}
        heading="Delete secret?"
        @close=${() => (this.pendingSecretDelete = "")}
      >
        <p>
          Delete <strong>${this.pendingSecretDelete}</strong>? Builds of this repository's branch
          workspaces will no longer receive it, unless the workspace or the team has a secret
          with the same name.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.pendingSecretDelete = "")}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="danger" @click=${() => this.confirmSecretDelete()}
          >Delete</bn-button
        >
      </bn-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-repository": PageRepository;
  }
}
